import gql from 'graphql-tag'
import { graphql, QueryProps } from 'react-apollo'

export const ProjectBasicDataFragment = gql`
  fragment ProjectParts on Project {
    id
    name
    description
    location
    designer
    company
    created
    modified
  }
`

export const BasicCaseDataFragment = gql`
  fragment CaseDataParts on ProjectCase {
    id
    projectId
    thumbnail
    name
    description
    lastReport
    lastResults
    spaceType
    parameters
  }
`

export const RECENT_PROJECTS_QUERY = gql`
  query RecentProjects {
    recentProjects(num: 3) {
      ...ProjectParts
    }
  }
  ${ProjectBasicDataFragment}
`
export const PROJECTS_QUERY = gql`
  query GetProjects {
    getProjects {
      ...ProjectParts

      spaceType
      parameters

      cases {
        ...CaseDataParts
      }
    }
  }
  ${ProjectBasicDataFragment}
  ${BasicCaseDataFragment}
`

export const GET_PROJECT_QUERY = gql`
  query Project($projectId: ID!) {
    project(id: $projectId) {
      ...ProjectParts

      spaceType
      parameters
      created
      modified
      activeCase
      cases {
        ...CaseDataParts
        data
      }
    }
  }
  ${ProjectBasicDataFragment}
  ${BasicCaseDataFragment}
`

export type ProjectCaseTypePartial = {
  id: string
  projectId: string
  thumbnail: string | null // URL of the autogenerated case thumbnail
  name: string
  description: string
  lastReport: string | null // URL of the last generated report, if available
  lastResults: string | null // ID of the last result set
  spaceType: string // space type and parameters used by this case. Parameters is a stringified JSON object
  parameters: string
}

export type ProjectType = {
  id: string
  name: string
  description: string
  location: string
  designer: string
  company: string
  cases: ProjectCaseTypePartial[]
  spaceType: string
  parameters: string
  created: string
  modified: string

  activeCase: string | null
}

export type RecentProjectType = {
  id: string
  name: string
  description: string
  location: string
  designer: string
  company: string
  created: string
  modified: string
}

type GetProjectInput = { projectId: string }

export type RecentProjectsResult = RecentProjectType[]

export type ProjectsResult = ProjectType[]

export type RecentProjectsProps = RecentProjectsResult & QueryProps
export type ProjectsProps = ProjectsResult & QueryProps
export type ProjectProps = ProjectType & QueryProps

export function getRecentProjects<T>() {
  return graphql<RecentProjectsResult, T, RecentProjectsProps>(
    RECENT_PROJECTS_QUERY,
    {
      props: props => ({
        ...props.data
      })
    }
  )
}

export function getProjects<T>() {
  return graphql<ProjectsResult, T, ProjectsProps>(PROJECTS_QUERY, {
    props: props => ({
      ...props.data
    })
  })
}

export const withProject = graphql<ProjectType, GetProjectInput, ProjectProps>(
  GET_PROJECT_QUERY,
  {
    options: (props: GetProjectInput) => ({
      variables: {
        projectId: props.projectId
      }
    }),
    props: props => ({
      ...props.data
    }),
    skip: props => !props.projectId
  }
)

export type ProjectInput = {
  name: string
  description: string
  location: string
  spaceType: string
}

export const UPDATE_PROJECT = gql`
  mutation UpdateProject(
    $projectId: ID!
    $project: ProjectInput
    $params: String
  ) {
    updateProject(projectId: $projectId, project: $project, params: $params) {
      ...ProjectParts

      parameters
      spaceType
    }
  }
  ${ProjectBasicDataFragment}
`

export const UPDATE_PROJECT_CONTACT = gql`
  mutation UpdateProjectContact(
    $projectId: ID!
    $designer: String
    $company: String
  ) {
    updateProjectContact(
      projectId: $projectId
      designer: $designer
      company: $company
    ) {
      ...ProjectParts
    }
  }
  ${ProjectBasicDataFragment}
`

export const CREATE_PROJECT = gql`
  mutation CreateProject(
    $project: ProjectInput!
    $params: String!
    $activeCase: String
    $cases: [CaseInput!]
  ) {
    createProject(
      project: $project
      params: $params
      activeCase: $activeCase
      cases: $cases
    ) {
      ...ProjectParts

      spaceType
      parameters
      activeCase
      cases {
        ...CaseDataParts
        data
      }
    }
  }
  ${ProjectBasicDataFragment}
  ${BasicCaseDataFragment}
`

export type CaseInput = {
  id: string
  name: string
  description: string
  data?: string
  new?: boolean
  delete?: boolean
}

export const SAVE_CASE = gql`
  mutation SaveCase($case: CaseInput!, $spaceType: String, $params: String) {
    saveCase(case: $case, spaceType: $spaceType, caseParams: $params) {
      ...CaseDataParts
    }
  }
  ${BasicCaseDataFragment}
`

export const SAVE_PROJECT_DATA = gql`
  mutation SaveProjectData(
    $projectId: ID!
    $activeCase: String
    $cases: [CaseInput!]!
  ) {
    saveProjectData(
      projectId: $projectId
      activeCase: $activeCase
      cases: $cases
    ) {
      ...ProjectParts

      spaceType
      parameters
      activeCase
      cases {
        ...CaseDataParts
        data
      }
    }
  }

  ${ProjectBasicDataFragment}
  ${BasicCaseDataFragment}
`

export const GET_REPORT_ACCESS_TOKEN = gql`
  mutation GetReportAccessToken {
    getReportAccessToken
  }
`



// WEBPACK FOOTER //
// ./src/Components/Projects/projectQueries.ts